import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Routes a participant to the live simulation screen the moment they are put on a team.
 *
 * This is the missing handoff between the normal session screens and the simulation:
 * the trainer starts a run and assigns teams from their console, but participants sit
 * on the lobby / active-session pages, which know nothing about simulations. Without
 * this hook their phones simply never change.
 *
 * Two signals cover the two ways a participant can arrive:
 *
 *  - `sim:teams-assigned` — broadcast to the whole session room when the trainer
 *    assigns. Filtered by participantId, because everyone in the room receives it.
 *  - `sim:state` — sent by the server ONLY to a socket whose participant already has a
 *    stored team, as its reply to `join-session`. This is the late-joiner/reconnect
 *    path: someone who joins after assignment never saw the broadcast, but every
 *    participant page emits `join-session` on mount, and the server answers with
 *    state if a team exists. No participantId check needed — receiving it at all
 *    means it is ours.
 *
 * When the navigation happens the host page unmounts and the listeners are cleaned up
 * with it. *
 * USES THE PAGE'S SOCKET — pass in the host page's own useWebSocket() result; never
 * call useWebSocket here. useWebSocket wires its connect/disconnect state to the FIRST
 * handle a page opens; any later handle reads `connected` once at mount and never
 * updates. A hook opening its own handle before the page's left the page's banner
 * stuck on "Reconnecting… / Offline / Live updates are not reaching this device"
 * after every refresh, while live updates were in fact arriving.
 *
 * @param socket         the host page's useWebSocket(sessionId) result
 * @param sessionId      the session the page is showing
 * @param participantId  this phone's participant, to pick out its own assignment
 */
const useSimulationAutoJoin = (socket, sessionId, participantId) => {
  const navigate = useNavigate();
  const on = socket?.on;
  const off = socket?.off;

  useEffect(() => {
    if (!sessionId || !on || !off) return undefined;

    const go = () => navigate(`/session/${sessionId}/simulation`);

    const onTeamsAssigned = ({ assignments }) => {
      if (!participantId) return;
      const mine = (assignments || []).some(
        (a) => String(a.participantId) === String(participantId)
      );
      if (mine) go();
    };

    const onState = (payload) => {
      // `closed` means the run is over — a stale team assignment must not drag a
      // participant back into a finished simulation.
      if (payload?.myFunctionKey && payload.phase && payload.phase !== 'closed') go();
    };

    on('sim:teams-assigned', onTeamsAssigned);
    on('sim:state', onState);
    return () => {
      off('sim:teams-assigned', onTeamsAssigned);
      off('sim:state', onState);
    };
  }, [sessionId, participantId, on, off, navigate]);
};

export default useSimulationAutoJoin;
